<template>
  <div class="image-face-overlay" aria-live="polite">
    <button
      v-for="face in faces"
      :key="face.id"
      type="button"
      class="image-face-box"
      :class="faceClass(face)"
      :style="faceStyle(face)"
      :aria-label="faceLabel(face)"
      :title="faceLabel(face)"
      @click.stop="$emit('select', face)"
    >
      <span class="image-face-label">{{ faceLabel(face) }}</span>
    </button>
    <div v-if="loading" class="image-face-message">
      <b-spinner small />
      <span>{{ $t('people_loading_faces') }}</span>
    </div>
    <div v-else-if="faces.length === 0" class="image-face-message">
      {{ $t('people_no_faces_in_photo') }}
    </div>
  </div>
</template>

<script>
export default {
  props: {
    faces: { type: Array, default: () => [] },
    selectedFaceId: { type: Number, default: null },
    loading: { type: Boolean, default: false },
  },
  methods: {
    faceStyle(face) {
      return {
        left: `${face.x * 100}%`,
        top: `${face.y * 100}%`,
        width: `${face.width * 100}%`,
        height: `${face.height * 100}%`,
      }
    },
    faceClass(face) {
      return {
        selected: face.id === this.selectedFaceId,
        confirmed: face.person?.status === 'confirmed',
        draft: face.person?.status === 'candidate',
        review: face.assignment_state === 'review',
        ignored: face.assignment_state === 'ignored',
        unassigned: !face.person && !['review', 'ignored'].includes(face.assignment_state),
      }
    },
    faceLabel(face) {
      const name = face.person?.name?.trim()
      if (name) return name
      if (face.assignment_state === 'ignored') return this.$t('people_ignored')
      if (face.assignment_state === 'review') return this.$t('people_needs_review')
      if (face.person?.status === 'candidate') return this.$t('people_unconfirmed')
      return this.$t('people_ungrouped')
    },
  },
}
</script>

<style scoped>
.image-face-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.image-face-box {
  position: absolute;
  padding: 0;
  border: 2px dashed rgba(255, 255, 255, .9);
  border-radius: 4px;
  background: transparent;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, .45);
  cursor: pointer;
  pointer-events: auto;
}

.image-face-box::after {
  position: absolute;
  inset: -7px;
  content: '';
}

.image-face-box.confirmed {
  border-style: solid;
  border-color: var(--highlight-color);
}

.image-face-box.draft,
.image-face-box.review {
  border-color: #f0ad4e;
}

.image-face-box.ignored {
  border-color: var(--faded-text-color);
  opacity: var(--faded-opacity);
}

.image-face-box.selected {
  border-style: solid;
  border-width: 3px;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, .7);
  opacity: 1;
}

.image-face-label {
  position: absolute;
  left: -2px;
  bottom: calc(100% + 5px);
  display: none;
  max-width: 220px;
  padding: 4px 8px;
  overflow: hidden;
  border-radius: 4px;
  background: rgba(var(--dark-bg-rgb), .92);
  color: var(--text-color);
  font-size: .85rem;
  line-height: 1.1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-face-box:hover .image-face-label,
.image-face-box.selected .image-face-label {
  display: block;
}

.image-face-message {
  position: absolute;
  top: 24px;
  left: 50%;
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 7px 12px;
  border-radius: 16px;
  background: rgba(var(--dark-bg-rgb), .85);
  color: var(--faded-text-color);
  transform: translateX(-50%);
}
</style>
